/**
 * The dental assistant at the chair (assistant role test 2026-10-20; module
 * id `chairside`, routes under `/v1/chairside`).
 *
 * - Turnover card: the next patient in my operatories or for my doctor, with
 *   the visit's procedures (teeth and surfaces), the tray and materials from
 *   the practice's setup table, medical alerts (premed first), the lab case
 *   for the visit, x-rays due, and a flag when the appointment type and the
 *   procedures disagree.
 * - Setup table: procedure code (or prefix) to tray and materials; Velora's
 *   defaults until the practice saves its own.
 * - "Complete for Dr. X": an assistant marks today's planned procedures done;
 *   they stay planned until the doctor approves (usually when signing the
 *   visit note), and approval completes them through the chart's own gated
 *   path (chart.post_complete). Nothing else is written to a PMS.
 * - Checkout check and the next-visit request: what is still not complete on
 *   today's visit, and a front-desk task to book the next visit when the
 *   assistant cannot book.
 */
import { z } from 'zod';

const Id = z.uuid();
const LocalDate = z.iso.date();
const Instant = z.string().min(1);

export const CHAIRSIDE_PERMISSIONS = {
  /** The turnover card, the visit summary and the setup table. */
  view: 'chart.view',
  /** Mark planned procedures done for the doctor. */
  mark: 'chart.edit',
  /** Approve marked procedures (completes them). */
  approve: 'chart.post_complete',
  /** Edit the setup table. */
  setup: 'settings.manage',
  /** Ask the front desk to book the next visit. */
  request: 'task.create',
} as const;

export const CHAIRSIDE_ENTITY = 'completion_request';

/* ------------------------------------------------------------------ */
/* Setup table                                                         */
/* ------------------------------------------------------------------ */

export const SetupMatchSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^D\d{1,4}\*$|^D\d{4}$/, 'A code like D2740 or a prefix like D27*');

export const SetupRuleSchema = z.object({
  match: SetupMatchSchema,
  tray: z.string().trim().min(1).max(80),
  materials: z.array(z.string().trim().min(1).max(120)).max(30),
  note: z.string().trim().max(300).nullable().optional(),
});
export type SetupRuleDto = z.infer<typeof SetupRuleSchema>;

export const SetupTableSchema = z.object({
  /** The practice's own rules (empty until saved). */
  rules: z.array(SetupRuleSchema),
  /** Velora's defaults, used for codes the practice has no rule for. */
  defaults: z.array(SetupRuleSchema),
  canEdit: z.boolean(),
});
export type SetupTable = z.infer<typeof SetupTableSchema>;

export const SaveSetupTableBodySchema = z.object({
  rules: z.array(SetupRuleSchema).max(300),
});

/* ------------------------------------------------------------------ */
/* Visits                                                              */
/* ------------------------------------------------------------------ */

export const VisitProcedureSchema = z.object({
  id: Id,
  code: z.string(),
  description: z.string().nullable(),
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
  /** OD ProcStat: 1 TP, 2 C, 8 TPi. */
  status: z.number().int(),
  /** `appointment`: attached to the visit; `plan`: a plan row that fits the visit (not attached). */
  source: z.enum(['appointment', 'plan']),
  /** Marked done by an assistant and waiting for the doctor. */
  pendingApproval: z.boolean(),
});
export type VisitProcedure = z.infer<typeof VisitProcedureSchema>;

export const ChairAlertSchema = z.object({
  kind: z.string(),
  description: z.string(),
  severity: z.string().nullable(),
});

export const VisitSetupSchema = z.object({
  tray: z.string(),
  codes: z.array(z.string()),
  materials: z.array(z.string()),
  notes: z.array(z.string()),
});

export const VisitLabCaseSchema = z.object({
  id: Id,
  caseNumber: z.string(),
  status: z.string(),
  labName: z.string().nullable(),
  dueDate: LocalDate.nullable(),
  /** The case is back from the lab (received or checked). */
  back: z.boolean(),
});

export const TurnoverVisitSchema = z.object({
  appointmentId: Id,
  patientId: Id,
  patientName: z.string(),
  startsAt: Instant,
  lengthMin: z.number().int(),
  operatoryId: Id.nullable(),
  providerId: Id.nullable(),
  providerName: z.string().nullable(),
  typeName: z.string().nullable(),
  flowStatus: z.string(),
  procedures: z.array(VisitProcedureSchema),
  setup: z.array(VisitSetupSchema),
  /** Every alert, premed first. */
  alerts: z.array(ChairAlertSchema),
  premedRequired: z.boolean(),
  labCase: VisitLabCaseSchema.nullable(),
  /** Radiograph kinds due today ("BWX", "FMX", "Pano"). */
  xraysDue: z.array(z.string()),
  /** Type and procedures disagree: one sentence, or null. */
  mismatch: z.string().nullable(),
});
export type TurnoverVisit = z.infer<typeof TurnoverVisitSchema>;

export const TurnoverRoomSchema = z.object({
  operatoryId: Id.nullable(),
  operatoryName: z.string(),
  /** In the chair now (or checking out). */
  current: TurnoverVisitSchema.nullable(),
  /** The next visit not yet seated. */
  next: TurnoverVisitSchema.nullable(),
});
export type TurnoverRoom = z.infer<typeof TurnoverRoomSchema>;

export const TurnoverQuerySchema = z.object({
  providerId: Id.optional(),
  /** Comma-separated operatory ids. */
  operatoryIds: z.string().max(2000).optional(),
});

export const TurnoverSchema = z.object({
  date: LocalDate,
  rooms: z.array(TurnoverRoomSchema),
});
export type Turnover = z.infer<typeof TurnoverSchema>;

/* ------------------------------------------------------------------ */
/* Visit summary, checkout check                                       */
/* ------------------------------------------------------------------ */

export const CompletionRequestSchema = z.object({
  id: Id,
  procedureId: Id,
  appointmentId: Id.nullable(),
  patientId: Id,
  code: z.string(),
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
  providerId: Id.nullable(),
  providerName: z.string().nullable(),
  requestedByName: z.string(),
  requestedAt: Instant,
  status: z.enum(['pending', 'approved', 'rejected', 'cancelled']),
});
export type CompletionRequest = z.infer<typeof CompletionRequestSchema>;

export const VisitSummarySchema = z.object({
  appointmentId: Id,
  patientId: Id,
  locationId: Id,
  providerId: Id.nullable(),
  providerName: z.string().nullable(),
  typeName: z.string().nullable(),
  lengthMin: z.number().int(),
  procedures: z.array(VisitProcedureSchema),
  pending: z.array(CompletionRequestSchema),
  /** Unsigned notes for this visit (draft, pending co-sign). */
  unsignedNotes: z.number().int(),
  /** Planned rows not on any upcoming visit: what the next visit could book. */
  remainingPlan: z.array(VisitProcedureSchema),
  /** A front-desk task to book the next visit is already open. */
  nextVisitRequested: z.boolean(),
  /** Post-op handout keys suggested from today's codes. */
  postOp: z.array(z.string()),
  canMark: z.boolean(),
  canApprove: z.boolean(),
  canRequest: z.boolean(),
});
export type VisitSummary = z.infer<typeof VisitSummarySchema>;

export const MarkDoneBodySchema = z.object({
  appointmentId: Id,
  procedureIds: z.array(Id).min(1).max(50),
});

export const ApproveBodySchema = z.object({
  ids: z.array(Id).min(1).max(50),
});

export const ApproveResultSchema = z.object({
  approved: z.array(Id),
  /** Requests that could not be completed, with the reason (another edit, permission). */
  failed: z.array(z.object({ id: Id, reason: z.string() })),
});
export type ApproveResult = z.infer<typeof ApproveResultSchema>;

export const CompletionListQuerySchema = z.object({
  appointmentId: Id.optional(),
  /** `mine`: waiting for the caller as the provider. */
  scope: z.enum(['mine', 'office']).optional(),
});

export const CompletionListSchema = z.object({
  requests: z.array(CompletionRequestSchema),
});

export const NextVisitBodySchema = z.object({
  appointmentId: Id,
  procedureIds: z.array(Id).max(50).default([]),
  lengthMin: z.number().int().min(5).max(600).nullable().optional(),
  providerId: Id.nullable().optional(),
  /** "in 2 weeks", "after the crown is back". */
  timing: z.string().trim().max(200).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type NextVisitBody = z.input<typeof NextVisitBodySchema>;

export const NextVisitResultSchema = z.object({
  taskId: Id,
  /** An open request for this visit already existed. */
  deduped: z.boolean(),
});
